<template>
  <default-layout>
    <main-container size="big" class="mailbox-settings-layout">
      <!-- HEADER -->
      <div class="mailbox-settings-layout__header">
        <i v-if="name" class="ddi ddi-mailbox" />
        <h3 @click="goToMailbox()">{{ name }}</h3>
        <dd-button :color="ColorAssistant.MailboxColor" size="medium" @click="goToMailbox()">
          <span>Go to Mailbox</span>&nbsp;
          <i class="ddi ddi-arrow-right" />
        </dd-button>
      </div>
      <div class="mailbox-settings-layout__body-wrapper">
        <!-- SIDEBAR -->
        <div class="mailbox-settings-layout__sidebar">
          <dd-sidebar :elements="sidebarElements" title="Mailbox Settings" />
        </div>
        <!-- MAIN -->
        <main class="mailbox-settings-layout__main">
          <slot />
        </main>
      </div>
    </main-container>
  </default-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import * as routerNames from '@/services/router/router.names'
import MainContainer from '@/layouts/main-container.vue'
import DefaultLayout from '@/layouts/default-layout.vue'

/* Mixins */

/* Components */

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { INavMenuGroup } from '@/types/data-objects/global'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'

export default defineComponent({
  name: 'MailboxSettingsLayout',

  components: {
    MainContainer,
    DefaultLayout,
  },

  props: {
    name: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      ColorAssistant,
      routerNames,
      isDirectConnection: true,
    }
  },

  computed: {
    sidebarElements(): INavMenuGroup[] {
      const items = [
        {
          label: 'Setup',
          options: [
            {
              value: routerNames.MAILBOX_SETTINGS_DIRECT_CONNECTION,
              label: 'Direct Connection',
              icon: 'link',
              isActive:
                this.$route.name ===
                routerNames.MAILBOX_SETTINGS_DIRECT_CONNECTION,
            },
            ...(this.isDirectConnection
              ? []
              : [
                  {
                    value: routerNames.MAILBOX_SETTINGS_AUTO_FORWARDING,
                    label: 'Auto-Forwarding',
                    icon: 'auto-forwarding',
                    isActive:
                      this.$route.name ===
                      routerNames.MAILBOX_SETTINGS_AUTO_FORWARDING,
                  },
                  {
                    value: routerNames.MAILBOX_SETTINGS_OUTGOING_EMAIL,
                    label: 'Outgoing Email',
                    icon: 'outgoing-email',
                    isActive:
                      this.$route.name ===
                      routerNames.MAILBOX_SETTINGS_OUTGOING_EMAIL,
                  },
                ]),
          ],
        },
        {
          label: 'Manage',
          options: [
            {
              value: routerNames.MAILBOX_SETTINGS_ASSIGN_PEOPLE,
              label: 'Add/Remove Users',
              icon: 'people',
              isActive:
                this.$route.name === routerNames.MAILBOX_SETTINGS_ASSIGN_PEOPLE,
            },
            {
              value: routerNames.MAILBOX_SETTINGS_CONVERSATION_RULES,
              label: 'Conversation Rules',
              icon: 'incoming-email',
              isActive:
                this.$route.name ===
                routerNames.MAILBOX_SETTINGS_CONVERSATION_RULES,
            },
            {
              value: routerNames.MAILBOX_SETTINGS_CHECKLIST_TEMPLATES,
              label: 'Checklist Templates',
              icon: 'everything',
              isActive:
                this.$route.name ===
                routerNames.MAILBOX_SETTINGS_CHECKLIST_TEMPLATES,
            },
            {
              value: routerNames.MAILBOX_SETTINGS_CUSTOM_FIELDS,
              label: 'Custom Fields',
              icon: 'three-blocks',
              isActive:
                this.$route.name === routerNames.MAILBOX_SETTINGS_CUSTOM_FIELDS,
            },
            {
              value: routerNames.MAILBOX_SETTINGS_SAVED_REPLIES,
              label: 'Saved Replies',
              icon: 'saved-replies',
              isActive:
                this.$route.name === routerNames.MAILBOX_SETTINGS_SAVED_REPLIES,
            },
            {
              value: routerNames.MAILBOX_SETTINGS_OFFICE_HOURS,
              label: 'Office Hours',
              icon: 'clock',
              isActive:
                this.$route.name === routerNames.MAILBOX_SETTINGS_OFFICE_HOURS,
            },
            {
              value: routerNames.MAILBOX_SETTINGS_AUTO_RESPONDER,
              label: 'Auto-Responder',
              icon: 'auto-responder',
              isActive:
                this.$route.name ===
                routerNames.MAILBOX_SETTINGS_AUTO_RESPONDER,
            },
            {
              value: routerNames.MAILBOX_SETTINGS_EMAIL_SIGNATURE,
              label: 'Email Signature',
              icon: 'edit',
              isActive:
                this.$route.name ===
                routerNames.MAILBOX_SETTINGS_EMAIL_SIGNATURE,
            },
            {
              value: routerNames.MAILBOX_SETTINGS_MANAGE_WORKFLOW,
              label: 'Manage Workflow',
              icon: 'workflows',
              isActive:
                this.$route.name ===
                routerNames.MAILBOX_SETTINGS_MANAGE_WORKFLOW,
            },
            {
              value: routerNames.MAILBOX_SETTINGS_WORKFLOW_AUTOMATIONS,
              label: 'Workflow Automations',
              icon: 'workflows',
              isActive:
                this.$route.name ===
                routerNames.MAILBOX_SETTINGS_WORKFLOW_AUTOMATIONS,
            },
            {
              value: routerNames.MAILBOX_SETTINGS_GENERAL_SETTINGS,
              label: 'General Settings',
              icon: 'cog',
              isActive:
                this.$route.name ===
                routerNames.MAILBOX_SETTINGS_GENERAL_SETTINGS,
            },
            {
              value: routerNames.MAILBOX_SETTINGS_INTEGRATIONS,
              label: 'Integrations',
              icon: 'integrations',
              isActive:
                this.$route.name === routerNames.MAILBOX_SETTINGS_INTEGRATIONS,
            },
          ],
        },
      ]

      /* Eventually, always add this option to menu and let AO click because the ticket portal page 
         itself will have the <feature-gated-stamp /> applied to it. */
      if (this.$permissions.ticketPortalFeatureEnabled) {
        items.find(i => i.label === 'Setup').options.push({
          value: routerNames.MAILBOX_SETTINGS_TICKET_PORTAL,
          label: 'Ticket Portal',
          icon: 'ticket-portal',
          isActive:
            this.$route.name === routerNames.MAILBOX_SETTINGS_TICKET_PORTAL,
        })
      }

      return items.filter(i => i.options.length)
    },
  },

  async created() {
    try {
      const response = await MailboxesAPI.get_GeneralSettings(
        this.$route.params.id,
      )
      this.isDirectConnection = response.isDirectConnection
    } catch (error) {
      this.$error.emit(error)
    }
  },

  methods: {
    goToMailbox() {
      RouteAssistant.goToMailbox(
        this.$router,
        this.$route.params.accountId,
        this.$route.params.id,
      )
    },
  },
})
</script>

<style lang="scss">
.mailbox-settings-layout {
  margin-bottom: 40px;

  &__header {
    display: flex;
    align-items: center;
    width: 100%;
    padding-bottom: 12px;
    margin: 0 16px 30px 16px;
    border-bottom: $border--solid-light;

    @include media-breakpoint-down(sm) {
      margin: 0 0 20px 0;
      padding-left: 16px;
      padding-right: 16px;
    }

    > .ddi {
      cursor: pointer;
      font-size: 18px;
      margin-right: 8px;
      color: var(--color-text-third);
    }

    h3 {
      cursor: pointer;
      margin-bottom: 0;
      @include single-line-ellipsis;
    }

    > :last-child {
      margin-left: auto;
    }
  }

  &__body-wrapper {
    display: flex;
    flex-direction: row;

    @include media-breakpoint-down(sm) {
      flex-direction: column;
    }
  }

  &__sidebar {
    flex: 0 0 224px;
    margin-right: 36px;

    @include media-breakpoint-down(sm) {
      flex: none;
      width: 100%;
      margin-right: 0;
      margin-bottom: 16px;
    }
  }

  &__main {
    flex-grow: 1;
    max-width: 750px;

    @include media-breakpoint-down(sm) {
      max-width: 100%;
    }

    h2 {
      margin: 0;
    }

    .el-radio {
      &__label {
        @include lettering--p;
      }

      .el-radio__input.is-checked + .el-radio__label {
        color: var(--color-text-secondary);
      }
    }

    .el-radio + .el-radio {
      margin-left: 0 !important;
    }

    .el-card {
      overflow: visible;
      .el-card__body {
        padding: 24px;
        .dd-divider {
          margin-top: 24px;
          margin-bottom: 24px;
        }
      }
    }
  }
}
</style>
